import { graphql } from 'react-relay';
import { useCallback, useMemo } from 'react';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import type { GraphQLError } from 'graphql';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type {
	useFeedFollowFeedFollowMutation as useFeedFollowFeedFollowMutation$data,
	useFeedFollowFeedFollowMutationVariables as useFeedFollowFeedFollowMutation$variables,
} from './__types__/useFeedFollowFeedFollowMutation.ts';
import type { useFeedFollowFeedFollowQuery as useFeedFollowFeedFollowQuery$data } from './__types__/useFeedFollowFeedFollowQuery.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
const useFeedFollowFeedFollowQuery = graphql`
	query useFeedFollowFeedFollowQuery {
		getFeedUserConfig {
			accountIds
		}
	}
`;
const useFeedFollowFeedFollowMutation = graphql`
	mutation useFeedFollowFeedFollowMutation($follow: [ID], $unfollow: [ID]) {
		setFeedUserConfig(input: { followUsers: $follow, unfollowUsers: $unfollow }) {
			success
		}
	}
`;
const isUserHasNoAccessError = (error: ApolloErrorInterop): boolean =>
	error?.graphQLErrors?.some(
		(gqlError: GraphQLError) =>
			gqlError?.path?.includes('getFeedUserConfig') &&
			gqlError?.message?.includes('User not permitted to access Confluence'),
	);

type FeedFollowData = {
	renderFeedFollow: boolean;
	getToggleFollowUser(
		userId: string | null | undefined,
		followed: boolean,
		source: string,
	): () => void;
	followedUsers: (string | null)[] | undefined;
	feedConfigError?: ApolloErrorInterop;
	mutationError?: ApolloErrorInterop;
	loading?: boolean;
};

export let skipQuery = false;

export function useFeedFollow(): FeedFollowData {
	const { isLicensed } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const {
		data,
		loading: queryLoading,
		error: feedConfigError,
	} = useQueryInterop<useFeedFollowFeedFollowQuery$data>(useFeedFollowFeedFollowQuery, {
		skip: !isLicensed || skipQuery,
		onError: (error: ApolloErrorInterop) => (skipQuery = isUserHasNoAccessError(error)),
	});
	const [mutateFeedFollow, { error: mutationError }] = useMutationInterop<
		useFeedFollowFeedFollowMutation$data,
		useFeedFollowFeedFollowMutation$variables
	>(useFeedFollowFeedFollowMutation, {
		refetchQueries: [
			{
				query: useFeedFollowFeedFollowQuery,
			},
		],
	});

	const followedUsersRaw = data?.getFeedUserConfig?.accountIds;
	const followedUsers: (string | null)[] | undefined = followedUsersRaw
		? followedUsersRaw.map((id) => id ?? null)
		: undefined;

	const getToggleFollowUser = useCallback(
		(accountId: string | null | undefined, following: boolean, source: string) => async () => {
			if (!accountId) {
				return;
			}

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'feedFollow',
					source,
					attributes: {
						id: accountId,
						settingType: 'user',
						actionType: following ? 'unfollow' : 'follow',
					},
				},
			}).fire();

			try {
				if (following) {
					await mutateFeedFollow({ variables: { unfollow: [accountId] } });
				} else {
					await mutateFeedFollow({ variables: { follow: [accountId] } });
				}
			} catch {
				// noop since the hook consumer logs the error through <ErrorDisplay>
				// mutateFeedFollow() has the potential to throw an error;
				// since we do not provide an onError callback to the useMutation hook
				// we need to catch the error here as a safety net.
			}
		},
		[mutateFeedFollow, createAnalyticsEvent],
	);

	return useMemo(
		() => ({
			renderFeedFollow: !(queryLoading || feedConfigError || skipQuery) && isLicensed,
			loading: queryLoading,
			getToggleFollowUser,
			feedConfigError,
			mutationError,
			followedUsers: followedUsers ?? [],
		}),
		[queryLoading, feedConfigError, isLicensed, getToggleFollowUser, mutationError, followedUsers],
	);
}
